import { memo } from 'react';
import { MenuTrigger } from 'react-aria-components';

import { merge } from '@/components';
import { Button, DateRangePicker, Menu, MenuItem, Popover, Text } from '@/components/core';
import { CalendarIcon } from '@/components/icons';
import { DateRangeColumnFilter, DateRangeConditions, useInboxStore } from '@/store';
import { generateId } from '@/utils';

import { FilterCard, FilterCardSubType } from './FilterCard.tsx';

export type DateRangeFilterCardProps = FilterCardSubType & {
  columns: string[];
  filter: DateRangeColumnFilter;
};

const IDateRangeFilterCard = ({ columns, filter, className, ...rest }: DateRangeFilterCardProps) => {
  const updateFilter = useInboxStore((state) => state.updateFilter);
  const deleteFilter = useInboxStore((state) => state.deleteFilter);

  return (
    <FilterCard onDelete={() => deleteFilter(filter.id)} className={merge('', className)} {...rest}>
      <MenuTrigger>
        <Button variant="plain">
          {filter.columnName ? (
            <>
              <CalendarIcon />
              <Text>{filter.columnName}</Text>
            </>
          ) : (
            <Text>Select column</Text>
          )}
        </Button>
        <Popover>
          <Menu>
            {columns
              .filter((c) => c !== filter.columnName)
              .filter((c) => ['createdTime', 'updatedTime'].includes(c))
              .map((c) => (
                <MenuItem key={c} onAction={() => updateFilter(filter.id, { columnName: c })}>
                  <Text>{c}</Text>
                </MenuItem>
              ))}
          </Menu>
        </Popover>
      </MenuTrigger>
      <MenuTrigger>
        <Button variant="plain">
          {filter.condition ? <Text>{filter.condition}</Text> : <Text>Select condition</Text>}
        </Button>
        <Popover>
          <Menu>
            {DateRangeConditions.map((condition) => (
              <MenuItem key={condition} onAction={() => updateFilter(filter.id, { condition })}>
                <Text>{condition}</Text>
              </MenuItem>
            ))}
          </Menu>
        </Popover>
      </MenuTrigger>
      <DateRangePicker
        label={filter.columnName ?? generateId()}
        dateRange={filter.value}
        onDateRangeChange={(value) => updateFilter(filter.id, { value })}
      />
    </FilterCard>
  );
};

export const DateRangeFilterCard = memo(IDateRangeFilterCard) as typeof IDateRangeFilterCard;
